import { t } from "i18next";
import { useCallback, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { deleteQuota } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations;
};

export const DeleteQuotaForm = ({ onCancel, onSuccess, quota }: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

	// The same two cases as the backend QuotaIsDeletableRule. The request validation gives the definite answer.
	// The invoice case applies to the advance invoice only. The invoices of an invoice-backed quota stay.
	const isAdvance = quota.type === "advance";
	const advanceInvoiceStatusId = isAdvance ? quota.invoices[0]?.invoiceStatusId : undefined;
	const advanceInvoiceIsSentOrPaid = advanceInvoiceStatusId === invoiceStatusId.SentToClient
		|| advanceInvoiceStatusId === invoiceStatusId.Paid;
	const hasConsumedBudget = quota.consumers.some((consumer) => consumer.budgetCentsConsumed > 0);
	const quotaIsDeletable = !advanceInvoiceIsSentOrPaid && !hasConsumedBudget;

	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await deleteQuota(quota.companyId, quota.id);
			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			if (apiError.type === ApiResponseErrorType.VALIDATION) {
				setServerErrorMessage(Object.values(apiError.messages)
					.flat()
					.map((detail) => detail.message)
					.join("\n"));
			} else {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setIsBusy(false);
		}
	}, [onSuccess, quota.companyId, quota.id]);

	return (
		<div
			className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungskontingent &quot;{quota.title}&quot; löschen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Löschen fehlgeschlagen">
						<span className="whitespace-pre-line">{serverErrorMessage}</span>
					</SidebarErrorOverlay>}
				{!quotaIsDeletable && <Hint theme="error"
											title="Löschen nicht möglich">
					{advanceInvoiceIsSentOrPaid &&
						<p>Die Vorauszahlungsrechnung wurde bereits versendet oder bezahlt.</p>}
					{hasConsumedBudget &&
						<p>Auf diesem Abrechnungskontingent wurden bereits Zeiten erfasst.</p>}
				</Hint>}
				{quotaIsDeletable &&
					<Hint>Beim Löschen eines Abrechnungskontingents werden alle verknüpften Bestellungen wieder
						freigegeben.{" "}
						{isAdvance
							? "Die Vorauszahlungsrechnung wird ebenfalls gelöscht."
							: "Die zugewiesenen Rechnungen bleiben erhalten. Ihre Verknüpfung mit dem Kontingent wird entfernt."}
					</Hint>}
				{quotaIsDeletable && showConfirmation &&
					<>
						<div className="h-4" />
						<Hint theme="error">Bist du Dir sicher? Das Löschen kann nicht rückgängig gemacht werden!</Hint>
					</>}
			</SidebarContent>
			<SidebarFooter>
				{!showConfirmation && <Button theme="danger"
											  onClick={() => setShowConfirmation(true)}
											  disabled={!quotaIsDeletable}>
					Abrechnungskontingent löschen
				</Button>}
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
				{showConfirmation && <Button theme="danger"
											 disabled={isBusy}
											 onClick={onSubmit}>
					Löschen bestätigen
				</Button>}
			</SidebarFooter>
		</div>);
};
